import { useState, useEffect } from 'react';
import { getAllCategories, Category } from '../utils/categories';
import { getCategoryEmoji } from '../utils/categoryIcons';
import { Plus, Search, ChevronDown, ChevronUp, Tag, Store } from 'lucide-react';
import api from '../services/api';

interface CategoryPickerProps {
  currentCategory?: string;
  onSelect: (category: string) => void;
  onClose: () => void;
  supermarketCategories?: string[];
}

export function CategoryPicker({ currentCategory, onSelect, onClose, supermarketCategories }: CategoryPickerProps) {
  const standardCategories = getAllCategories();
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustom, setShowCustom] = useState(true);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    loadCustomCategories();
  }, []);

  const loadCustomCategories = async () => {
    try {
      // Load from backend (user's category mappings + supermarkets)
      const response = await api.get('/items/user-categories');
      const backendCategories = response.data.categories || [];
      
      // Load from localStorage (persistently saved custom categories)
      const savedCategories = localStorage.getItem('customCategories');
      const localCategories = savedCategories ? JSON.parse(savedCategories) : [];
      
      // Merge and deduplicate
      const allCategories = [...new Set([...backendCategories, ...localCategories])];
      
      setCustomCategories(allCategories.sort());
    } catch (error) {
      console.error('Error loading custom categories:', error);
      // Fallback to localStorage only
      const savedCategories = localStorage.getItem('customCategories');
      setCustomCategories(savedCategories ? JSON.parse(savedCategories) : []);
    }
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    
    const trimmed = newCategoryName.trim();
    const allCategories = [...standardCategories.map(([name]) => name), ...customCategories];
    
    if (allCategories.includes(trimmed)) {
      return; // Already exists
    }
    
    // Add to state
    const updatedCategories = [...customCategories, trimmed].sort();
    setCustomCategories(updatedCategories);
    
    // Save to localStorage for persistence
    localStorage.setItem('customCategories', JSON.stringify(updatedCategories));
    
    onSelect(trimmed);
    onClose();
  };

  // Filter categories based on search
  const filteredStandard = standardCategories.filter(([name]) => 
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredCustom = customCategories.filter(name => 
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isInSupermarket = (categoryName: string) => {
    return supermarketCategories?.includes(categoryName) || false;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: 'var(--shadow-xl)',
          color: 'var(--text-primary)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
          Kategorie wählen
        </h3>
        
        {/* Search Field */}
        <div style={{ position: 'relative', marginBottom: '1rem' }}>
          <Search 
            size={18} 
            style={{ 
              position: 'absolute', 
              left: '0.75rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'var(--text-tertiary)'
            }} 
          />
          <input
            type="text"
            placeholder="Kategorie suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none'
            }}
            autoFocus
          />
        </div>

        {/* Supermarket Info */}
        {supermarketCategories && supermarketCategories.length > 0 && (
          <div style={{
            backgroundColor: 'var(--info-light)',
            border: '1px solid var(--primary)',
            borderRadius: '8px',
            padding: '0.75rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            color: 'var(--primary)'
          }}>
            <Store size={16} />
            <span>
              Kategorien mit <Store size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> sind im Supermarkt vorhanden
            </span>
          </div>
        )}

        {/* Standard Categories */}
        {filteredStandard.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ 
              fontSize: '0.75rem', 
              fontWeight: '600', 
              color: 'var(--text-tertiary)', 
              marginBottom: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Standard-Kategorien
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                gap: '0.75rem'
              }}
            >
              {filteredStandard.map(([categoryName, category]) => {
                const IconComponent = category.icon;
                const isSelected = currentCategory === categoryName;
                const inSupermarket = isInSupermarket(categoryName);
                
                return (
                  <button
                    key={categoryName}
                    onClick={() => {
                      onSelect(categoryName);
                      onClose();
                    }}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: isSelected ? '2px solid var(--primary)' : '2px solid var(--border-color)',
                      borderRadius: '8px',
                      backgroundColor: isSelected ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '0.875rem',
                      fontWeight: isSelected ? '600' : '500',
                      color: 'var(--text-primary)'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {inSupermarket && (
                      <div style={{
                        position: 'absolute',
                        top: '0.25rem',
                        right: '0.25rem',
                        backgroundColor: 'var(--primary)',
                        borderRadius: '50%',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Im Supermarkt vorhanden"
                      >
                        <Store size={12} color="white" strokeWidth={2.5} />
                      </div>
                    )}
                    <IconComponent 
                      size={32} 
                      color={category.color} 
                      strokeWidth={2}
                    />
                    <span style={{ textAlign: 'center', lineHeight: '1.2' }}>
                      {category.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Custom Categories */}
        {customCategories.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={() => setShowCustom(!showCustom)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '0.5rem 0',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: showCustom ? '0.75rem' : 0
              }}
            >
              <span>Eigene Kategorien ({customCategories.length})</span>
              {showCustom ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            
            {showCustom && filteredCustom.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                  gap: '0.75rem'
                }}
              >
                {filteredCustom.map((categoryName) => {
                  const isSelected = currentCategory === categoryName;
                  const inSupermarket = isInSupermarket(categoryName);
                  
                  return (
                    <button
                      key={categoryName}
                      onClick={() => {
                        onSelect(categoryName);
                        onClose();
                      }}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.75rem',
                        border: isSelected ? '2px solid var(--primary)' : '2px solid var(--border-color)',
                        borderRadius: '8px',
                        backgroundColor: isSelected ? 'var(--info-light)' : 'var(--bg-secondary)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        fontSize: '0.875rem',
                        fontWeight: isSelected ? '600' : '500',
                        color: 'var(--text-primary)',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                        }
                      }}
                    >
                      {inSupermarket && (
                        <div style={{
                          position: 'absolute',
                          top: '0.25rem',
                          right: '0.25rem',
                          backgroundColor: 'var(--primary)',
                          borderRadius: '50%',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Im Supermarkt vorhanden"
                        >
                          <Store size={12} color="white" strokeWidth={2.5} />
                        </div>
                      )}
                      <span style={{ fontSize: '1.25rem', marginRight: '0.5rem' }}>
                        {getCategoryEmoji(categoryName)}
                      </span>
                      {categoryName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Add New Category */}
        {!showNewCategory ? (
          <button
            onClick={() => setShowNewCategory(true)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px dashed var(--primary)',
              borderRadius: '8px',
              backgroundColor: 'var(--bg-primary)',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: 'var(--primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}
          >
            <Plus size={18} />
            Neue Kategorie
          </button>
        ) : (
          <div style={{ 
            padding: '1rem', 
            backgroundColor: 'var(--bg-secondary)', 
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            <input
              type="text"
              placeholder="Kategoriename eingeben..."
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                marginBottom: '0.5rem',
                outline: 'none'
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim()}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: newCategoryName.trim() ? 'var(--primary)' : 'var(--border-color)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: newCategoryName.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Hinzufügen
              </button>
              <button
                onClick={() => {
                  setShowNewCategory(false);
                  setNewCategoryName('');
                }}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            marginTop: '1.5rem',
            width: '100%',
            padding: '0.75rem',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            backgroundColor: 'var(--bg-secondary)',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            color: 'var(--text-primary)'
          }}
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}
